import {i18n} from "../../shared/i18n";
export class OnboardingContractError extends Error {
    constructor(message, requestId = null) {
        super(message);
        this.name = "OnboardingContractError";
        this.requestId = requestId;
    }
}

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const object = (value, label) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new OnboardingContractError(`${label} must be an object`);
    }
    return value;
};

const field = (value, key, label = key) => {
    if (!hasOwn(value, key)) {
        throw new OnboardingContractError(`${label} is required`);
    }
    return value[key];
};

const numberValue = (value, label) => {
    if (
        (typeof value !== "number" && typeof value !== "string") ||
        (typeof value === "string" && value.trim() === "")
    ) {
        throw new OnboardingContractError(`${label} must be an integer`);
    }
    return Number(value);
};

const positiveId = (value, label) => {
    const number = numberValue(value, label);
    if (!Number.isInteger(number) || number <= 0) {
        throw new OnboardingContractError(`${label} must be a positive integer`);
    }
    return number;
};

const nullableId = (value, label) =>
    value === null || value === "" ? null : positiveId(value, label);

const nonNegativeInteger = (value, label) => {
    const number = numberValue(value, label);
    if (!Number.isInteger(number) || number < 0) {
        throw new OnboardingContractError(`${label} must be a non-negative integer`);
    }
    return number;
};

const isoTimestamp = (value, label, nullable = false) => {
    if (nullable && (value === null || value === "")) return null;
    const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
    if (typeof value !== "string" || !isoPattern.test(value) || Number.isNaN(Date.parse(value))) {
        throw new OnboardingContractError(`${label} must be an ISO timestamp`);
    }
    return value;
};

const text = (value, label) => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "object" || typeof value === "function" || typeof value === "symbol") {
        throw new OnboardingContractError(`${label} must be text or null`);
    }
    const normalized = String(value);
    return normalized.trim() === "" ? null : normalized;
};

const requiredText = (value, label) => {
    if (typeof value !== "string" || value.trim() === "") {
        throw new OnboardingContractError(`${label} must be non-empty text`);
    }
    return value.trim();
};

const boolean = (value, label) => {
    if (typeof value !== "boolean") {
        throw new OnboardingContractError(`${label} must be a boolean`);
    }
    return value;
};

const array = (value, label) => {
    if (!Array.isArray(value)) {
        throw new OnboardingContractError(`${label} must be an array`);
    }
    return value;
};

const pageNumber = (value, label) => {
    if (value === null) return null;
    return positiveId(value, label);
};

const normalizeActor = (value, label, nullable = false) => {
    if (nullable && value === null) return null;
    const actor = object(value, label);
    return {
        id: positiveId(field(actor, "id", `${label}.id`), `${label}.id`),
        name: requiredText(field(actor, "name", `${label}.name`), `${label}.name`),
        role: requiredText(field(actor, "role", `${label}.role`), `${label}.role`),
    };
};

const normalizeEvidence = (value, label = "evidence", nullable = false) => {
    if (nullable && value === null) return null;
    const evidence = object(value, label);
    return {
        action: requiredText(field(evidence, "action", `${label}.action`), `${label}.action`),
        actor_id: positiveId(
            field(evidence, "actor_id", `${label}.actor_id`),
            `${label}.actor_id`,
        ),
        actor_name: requiredText(
            field(evidence, "actor_name", `${label}.actor_name`),
            `${label}.actor_name`,
        ),
        actor_role: requiredText(
            field(evidence, "actor_role", `${label}.actor_role`),
            `${label}.actor_role`,
        ),
        created_at: isoTimestamp(
            field(evidence, "created_at", `${label}.created_at`),
            `${label}.created_at`,
        ),
        request_id: requiredText(
            field(evidence, "request_id", `${label}.request_id`),
            `${label}.request_id`,
        ),
    };
};

const RESOURCE_KEYS = [
    "created_user_id",
    "created_place_id",
    "created_branch_id",
    "created_company_id",
    "created_organization_branch_id",
    "created_staff_member_id",
];

const normalizeResources = (value, label, requireAll = false) => {
    const resources = object(value, label);
    return RESOURCE_KEYS.reduce((result, key) => {
        if (requireAll && !hasOwn(resources, key)) {
            throw new OnboardingContractError(`${label}.${key} is required`);
        }
        result[key] = hasOwn(resources, key)
            ? nullableId(resources[key], `${label}.${key}`)
            : null;
        return result;
    }, {});
};

const normalizeListItem = (value, {allowLegacyAliases = true} = {}) => {
    const item = object(value, "application");
    const cityNameValue = hasOwn(item, "city_name")
        ? item.city_name
        : allowLegacyAliases
          ? field(item, "city", "city_name")
          : field(item, "city_name");
    const submittedAtValue = hasOwn(item, "submitted_at")
        ? item.submitted_at
        : allowLegacyAliases
          ? field(item, "created_at", "submitted_at")
          : field(item, "submitted_at");

    return {
        id: positiveId(field(item, "id"), "id"),
        status: requiredText(field(item, "status"), "status"),
        flow_type: requiredText(field(item, "flow_type"), "flow_type"),
        display_name: text(field(item, "display_name"), "display_name"),
        company_name: text(field(item, "company_name"), "company_name"),
        applicant_name: text(field(item, "applicant_name"), "applicant_name"),
        phone_number: text(field(item, "phone_number"), "phone_number"),
        email: text(field(item, "email"), "email"),
        country_id: nullableId(field(item, "country_id"), "country_id"),
        country_name: text(field(item, "country_name"), "country_name"),
        city_id: nullableId(field(item, "city_id"), "city_id"),
        city_name: text(cityNameValue, "city_name"),
        sales_manager_id: nullableId(field(item, "sales_manager_id"), "sales_manager_id"),
        sales_manager_name: text(field(item, "sales_manager_name"), "sales_manager_name"),
        revision: positiveId(field(item, "revision"), "revision"),
        documents_count: nonNegativeInteger(field(item, "documents_count"), "documents_count"),
        rejected_documents_count: nonNegativeInteger(
            field(item, "rejected_documents_count"),
            "rejected_documents_count",
        ),
        submitted_at: isoTimestamp(submittedAtValue, "submitted_at"),
        updated_at: isoTimestamp(field(item, "updated_at"), "updated_at"),
    };
};

const normalizeDocument = (value, index) => {
    const label = `documents[${index}]`;
    const documentRow = object(value, label);
    return {
        id: positiveId(field(documentRow, "id", `${label}.id`), `${label}.id`),
        document_type: requiredText(
            field(documentRow, "document_type", `${label}.document_type`),
            `${label}.document_type`,
        ),
        status: requiredText(
            field(documentRow, "status", `${label}.status`),
            `${label}.status`,
        ),
        original_filename: requiredText(
            field(documentRow, "original_filename", `${label}.original_filename`),
            `${label}.original_filename`,
        ),
        content_type: requiredText(
            field(documentRow, "content_type", `${label}.content_type`),
            `${label}.content_type`,
        ),
        size_bytes: nonNegativeInteger(
            field(documentRow, "size_bytes", `${label}.size_bytes`),
            `${label}.size_bytes`,
        ),
        sha256: requiredText(
            field(documentRow, "sha256", `${label}.sha256`),
            `${label}.sha256`,
        ),
        rejection_reason: text(
            field(documentRow, "rejection_reason", `${label}.rejection_reason`),
            `${label}.rejection_reason`,
        ),
        created_at: isoTimestamp(
            field(documentRow, "created_at", `${label}.created_at`),
            `${label}.created_at`,
        ),
        updated_at: isoTimestamp(
            field(documentRow, "updated_at", `${label}.updated_at`),
            `${label}.updated_at`,
        ),
    };
};

const normalizeCardEnrichment = (value, label = "card_enrichment") => {
    if (value === null) return null;
    const enrichment = object(value, label);
    const applied = object(
        field(enrichment, "applied_fields", `${label}.applied_fields`),
        `${label}.applied_fields`,
    );
    const suggestions = object(
        field(enrichment, "suggestions", `${label}.suggestions`),
        `${label}.suggestions`,
    );
    const applied_fields = {};
    for (const [key, entry] of Object.entries(applied)) {
        const textValue = text(entry, `${label}.applied_fields.${key}`);
        if (textValue) applied_fields[key] = textValue;
    }
    const suggestion_fields = {};
    for (const [key, entry] of Object.entries(suggestions)) {
        const textValue = text(entry, `${label}.suggestions.${key}`);
        if (textValue) suggestion_fields[key] = textValue;
    }
    return {
        status: requiredText(field(enrichment, "status", `${label}.status`), `${label}.status`),
        provider: text(field(enrichment, "provider", `${label}.provider`), `${label}.provider`),
        model: text(field(enrichment, "model", `${label}.model`), `${label}.model`),
        applied_fields,
        suggestions: suggestion_fields,
        notes: text(field(enrichment, "notes", `${label}.notes`), `${label}.notes`),
        error: text(field(enrichment, "error", `${label}.error`), `${label}.error`),
    };
};

const normalizeBlockingIssue = (value, index) => {
    const label = `blocking_issues[${index}]`;
    const issue = object(value, label);
    return {
        code: requiredText(field(issue, "code", `${label}.code`), `${label}.code`),
        message: requiredText(field(issue, "message", `${label}.message`), `${label}.message`),
        field: text(field(issue, "field", `${label}.field`), `${label}.field`),
    };
};

export const normalizeApplicationList = (payload) => {
    const page = object(payload, "application list");
    return {
        count: nonNegativeInteger(field(page, "count"), "count"),
        next: pageNumber(field(page, "next"), "next"),
        previous: pageNumber(field(page, "previous"), "previous"),
        results: array(field(page, "results"), "results").map((item) => normalizeListItem(item)),
    };
};

export const normalizeApplicationDetail = (payload) => {
    const detail = object(payload, "application detail");
    const listFields = normalizeListItem(detail, {allowLegacyAliases: false});

    return {
        ...listFields,
        first_name: text(field(detail, "first_name"), "first_name"),
        last_name: text(field(detail, "last_name"), "last_name"),
        company_type: text(field(detail, "company_type"), "company_type"),
        bin_number: text(field(detail, "bin_number"), "bin_number"),
        director_full_name: text(field(detail, "director_full_name"), "director_full_name"),
        legal_address: text(field(detail, "legal_address"), "legal_address"),
        actual_address: text(field(detail, "actual_address"), "actual_address"),
        category_id: nullableId(field(detail, "category_id"), "category_id"),
        category_name: text(field(detail, "category_name"), "category_name"),
        description: text(field(detail, "description"), "description"),
        instagram: text(field(detail, "instagram"), "instagram"),
        gis_link: text(field(detail, "gis_link"), "gis_link"),
        bank_name: text(field(detail, "bank_name"), "bank_name"),
        bik: text(field(detail, "bik"), "bik"),
        account_number: text(field(detail, "account_number"), "account_number"),
        moderation_comment: text(
            field(detail, "moderation_comment"),
            "moderation_comment",
        ),
        card_enrichment: hasOwn(detail, "card_enrichment")
            ? normalizeCardEnrichment(detail.card_enrichment, "card_enrichment")
            : null,
        moderated_by: normalizeActor(field(detail, "moderated_by"), "moderated_by", true),
        moderated_at: isoTimestamp(field(detail, "moderated_at"), "moderated_at", true),
        documents: array(field(detail, "documents"), "documents").map(normalizeDocument),
        public_access_token_issued: boolean(
            field(detail, "public_access_token_issued"),
            "public_access_token_issued",
        ),
        allowed_actions: array(field(detail, "allowed_actions"), "allowed_actions").map(
            (action, index) => requiredText(action, `allowed_actions[${index}]`),
        ),
        blocking_issues: array(field(detail, "blocking_issues"), "blocking_issues").map(
            normalizeBlockingIssue,
        ),
        resources: normalizeResources(detail, "resources", true),
        published_at: isoTimestamp(field(detail, "published_at"), "published_at", true),
        credentials_issued_at: isoTimestamp(
            field(detail, "credentials_issued_at"),
            "credentials_issued_at",
            true,
        ),
        action_evidence: normalizeEvidence(
            field(detail, "action_evidence"),
            "action_evidence",
            true,
        ),
    };
};

const normalizeHistoryEvent = (value, index) => {
    const label = `results[${index}]`;
    const event = object(value, label);
    return {
        id: positiveId(field(event, "id", `${label}.id`), `${label}.id`),
        action: requiredText(field(event, "action", `${label}.action`), `${label}.action`),
        from_status: text(
            field(event, "from_status", `${label}.from_status`),
            `${label}.from_status`,
        ),
        to_status: text(field(event, "to_status", `${label}.to_status`), `${label}.to_status`),
        comment: text(field(event, "comment", `${label}.comment`), `${label}.comment`),
        actor: normalizeActor(field(event, "actor", `${label}.actor`), `${label}.actor`),
        created_at: isoTimestamp(
            field(event, "created_at", `${label}.created_at`),
            `${label}.created_at`,
        ),
        request_id: requiredText(
            field(event, "request_id", `${label}.request_id`),
            `${label}.request_id`,
        ),
        resources: normalizeResources(
            field(event, "resource_ids", `${label}.resource_ids`),
            `${label}.resource_ids`,
        ),
    };
};

export const normalizeHistoryPage = (payload) => {
    const page = object(payload, "history page");
    return {
        count: nonNegativeInteger(field(page, "count"), "count"),
        next: pageNumber(field(page, "next"), "next"),
        previous: pageNumber(field(page, "previous"), "previous"),
        results: array(field(page, "results"), "results").map(normalizeHistoryEvent),
    };
};

export const normalizeActionResponse = (payload) => {
    const response = object(payload, "action response");
    return {
        application: normalizeApplicationDetail(field(response, "application")),
        evidence: normalizeEvidence(field(response, "evidence")),
    };
};

const normalizeCredentials = (value) =>
    array(value, "initial_credentials").map((credential, index) => {
        const label = `initial_credentials[${index}]`;
        const row = object(credential, label);
        const scope = requiredText(field(row, "scope", `${label}.scope`), `${label}.scope`);
        if (scope !== "owner" && scope !== "branch") {
            throw new OnboardingContractError(`${label}.scope must be owner or branch`);
        }
        return {
            scope,
            login: requiredText(field(row, "login", `${label}.login`), `${label}.login`),
            password: requiredText(
                field(row, "password", `${label}.password`),
                `${label}.password`,
            ),
        };
    });

const extractCredentialEnvelope = (payload, title) => {
    const response = object(payload, "credential response");
    const publicResult = normalizeActionResponse(response);
    const values = normalizeCredentials(field(response, "initial_credentials"));
    return {
        publicResult,
        secretEnvelope:
            values.length === 0
                ? null
                : {
                      kind: "credentials",
                      title,
                      issuedAt: publicResult.application.credentials_issued_at,
                      values,
                  },
    };
};

export const extractPublishSecrets = (payload) =>
    extractCredentialEnvelope(payload, i18n.t("sections.onboarding.p216"));

export const extractCredentialSecrets = (payload) =>
    extractCredentialEnvelope(payload, i18n.t("sections.onboarding.p217"));

export const extractAccessTokenSecret = (payload) => {
    const response = object(payload, "access token response");
    const applicationId = positiveId(field(response, "application_id"), "application_id");
    const revision = positiveId(field(response, "revision"), "revision");
    const issuedAt = isoTimestamp(field(response, "issued_at"), "issued_at");
    const evidence = normalizeEvidence(field(response, "evidence"));
    const rawToken = field(response, "access_token");
    const token = rawToken === null ? null : requiredText(rawToken, "access_token");

    return {
        publicResult: {
            applicationPatch: {
                id: applicationId,
                revision,
                public_access_token_issued: true,
            },
            evidence,
        },
        secretEnvelope:
            token === null
                ? null
                : {
                      kind: "access_token",
                      title: i18n.t("sections.onboarding.p218"),
                      issuedAt,
                      values: [{scope: "applicant", token}],
                  },
    };
};
